/**
 * Normalizes API responses to handle different response structures
 *
 * The API returns responses in this format:
 * {
 *   data: [...] or {...},
 *   success: boolean,
 *   warning: boolean,
 *   message: string,
 *   count: number
 * }
 *
 * This helper extracts the actual data from the response
 */

/**
 * Extracts data from normalized API response
 * @param {Object} response - The API response object
 * @returns {*} The extracted data
 */
export const normalizeApiResponse = (response) => {
  // If response is null or undefined, return it as is
  if (!response) {
    return response;
  }

  // If response has a 'data' property, return it
  if (Object.prototype.hasOwnProperty.call(response, 'data')) {
    return response.data;
  }

  // Otherwise, return the response as is (for backward compatibility)
  return response;
};

/**
 * Checks if API response indicates success
 * @param {Object} response - The API response object
 * @returns {boolean} True if successful
 */
export const isApiResponseSuccessful = (response) => {
  if (!response) {
    return false;
  }

  // Check for explicit success flag
  if (Object.prototype.hasOwnProperty.call(response, 'success')) {
    return response.success === true;
  }

  // Check for status flag (legacy)
  if (Object.prototype.hasOwnProperty.call(response, 'status')) {
    return response.status === true;
  }

  // If no success/status flag, assume success if data exists
  return Object.prototype.hasOwnProperty.call(response, 'data');
};

/**
 * Gets the message from API response
 * @param {Object} response - The API response object
 * @returns {string} The message
 */
export const getApiResponseMessage = (response) => {
  if (!response) {
    return '';
  }

  return response.message || response.developerMessage || '';
};

/**
 * Handles API response for staff endpoint specifically
 * Staff endpoint returns either an array or { data: [...] }
 * @param {Object|Array} response - The API response
 * @returns {Array} Array of staff data
 */
export const normalizeStaffResponse = (response) => {
  if (!response) {
    return [];
  }

  // If it's already an array, return it
  if (Array.isArray(response)) {
    return response;
  }

  // If it has a data property that's an array, return it
  if (response.data && Array.isArray(response.data)) {
    return response.data;
  }

  // If it has a data property that's an object with a list, return the list
  if (response.data && response.data.staffList) {
    return response.data.staffList;
  }

  // If response itself has a staffList, return it
  if (response.staffList) {
    return response.staffList;
  }

  // Return as single-item array
  return [response];
};
